import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function RejectionsTab() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [correctionNote, setCorrectionNote] = useState('');
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [unmatched, setUnmatched] = useState([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/rcm/rejections/queue');
      setQueue(r.data.queue || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadUnmatched = async () => {
    setUnmatchedLoading(true);
    try {
      const r = await api.get('/rcm/external-responses/unmatched');
      setUnmatched(r.data.events || []);
    } catch { setUnmatched([]); }
    finally { setUnmatchedLoading(false); }
  };

  const toggleUnmatched = () => {
    if (!showUnmatched) loadUnmatched();
    setShowUnmatched(!showUnmatched);
  };

  const openDetail = async (rejId) => {
    setSelected(rejId);
    setCorrectionNote('');
    try {
      const r = await api.get(`/rcm/rejections/${rejId}`);
      setDetail(r.data);
    } catch { setDetail(null); }
  };

  const doAction = async (action, extra = {}) => {
    if (!selected) return;
    try {
      await api.post(`/rcm/rejections/${selected}/${action}`, extra);
      load(); openDetail(selected);
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="rejections-tab">
      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Rejection Queue ({queue.length})</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={toggleUnmatched} data-testid="toggle-unmatched">
              <AlertTriangle className="h-3 w-3 mr-1" />{showUnmatched ? 'Hide' : 'Show'} Unmatched
            </Button>
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
          </div>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {queue.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No rejections in queue</p> : (
          <table className="w-full text-xs" data-testid="rejection-table">
            <thead><tr className="border-b text-left">
              <th className="py-1.5 font-medium text-muted-foreground">Code</th>
              <th className="py-1.5 font-medium text-muted-foreground">Reason</th>
              <th className="py-1.5 font-medium text-muted-foreground">Payer</th>
              <th className="py-1.5 font-medium text-muted-foreground">Source</th>
              <th className="py-1.5 font-medium text-muted-foreground">Status</th>
              <th className="py-1.5"></th>
            </tr></thead>
            <tbody>{queue.map((r) => (
              <tr key={r.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(r.id)}>
                <td className="py-1.5 font-mono font-medium">{r.rejection_reason_code}</td>
                <td className="py-1.5 truncate max-w-[200px]">{r.rejection_reason_text}</td>
                <td className="py-1.5">{r._payer_name || '—'}</td>
                <td className="py-1.5"><ExternalSourceBadge source={r.source} /></td>
                <td className="py-1.5"><StatusChip status={r.resolution_status} size="xs" /></td>
                <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
              </tr>
            ))}</tbody>
          </table>)}
        </CardContent>
      </Card>

      {/* ═══ PHASE 6 Slice 2: Unmatched Responses Queue ═══ */}
      {showUnmatched && (
        <Card className="border-amber-200 bg-amber-50/10" data-testid="unmatched-queue">
          <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3 text-amber-600" />Unmatched External Responses ({unmatched.length})
            </CardTitle>
            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={loadUnmatched}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {unmatchedLoading ? <p className="text-xs text-muted-foreground py-2">Loading...</p> :
             unmatched.length === 0 ? <p className="text-xs text-muted-foreground py-2 text-center">No unmatched responses</p> : (
              <div className="space-y-2">
                {unmatched.map((ev) => (
                  <div key={ev.id} className="border rounded p-2 text-xs space-y-1" data-testid={`unmatched-${ev.id?.slice(0,8)}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{ev.vendor_name}</span>
                        <EnvironmentBadge env={ev.environment} />
                        <span className="text-amber-600 font-medium">{ev.matching_status}</span>
                      </div>
                      <span className="text-muted-foreground text-[10px]">{ev.received_at ? new Date(ev.received_at).toLocaleString() : '—'}</span>
                    </div>
                    {ev.external_reference_id && <div className="text-muted-foreground">Ref: <span className="font-mono">{ev.external_reference_id}</span></div>}
                    {ev.parsed_ack && (
                      <div className="flex gap-2">
                        <span className="font-mono text-red-600">{ev.parsed_ack.parsed_code}</span>
                        <span>{ev.parsed_ack.parsed_summary}</span>
                      </div>
                    )}
                    {ev.exception_reason && <div className="text-red-600">{ev.exception_reason}</div>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <SlidePanel open={!!selected} onClose={() => { setSelected(null); setDetail(null); }} title="Rejection Detail">
        {detail && (<>
          {/* Source facts (immutable) */}
          <Card className="border-red-200 bg-red-50/30">
            <CardContent className="p-3 space-y-1 text-xs">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-red-700">Rejection Reason (source fact)</p>
                <ExternalSourceBadge source={detail.source} />
              </div>
              <p className="font-mono font-medium">{detail.rejection_reason_code}</p>
              <p>{detail.rejection_reason_text}</p>
              <p className="text-muted-foreground">Category: {detail.rejection_category} | Source: {detail.source}</p>
              <p className="text-muted-foreground">Rejected: {detail.rejected_at?.slice(0,10)}</p>
              {/* Raw response viewer for external sources */}
              {detail.source === 'external' && detail.external_event_id && (
                <RawResponseViewer eventId={detail.external_event_id} />
              )}
            </CardContent>
          </Card>

          {/* Current status + actions */}
          <Card><CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">Current:</span>
              <StatusChip status={detail.resolution_status} />
            </div>
            <div className="flex flex-wrap gap-2">
              {detail.resolution_status === 'open' && (
                <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => doAction('mark-under-review')} data-testid="action-review">
                  <Eye className="h-3 w-3 mr-1" />Start Review
                </Button>
              )}
              {detail.resolution_status === 'under_review' && (<>
                <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => doAction('mark-corrected', { correction_note: correctionNote })} data-testid="action-correct">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Mark Corrected
                </Button>
              </>)}
              {detail.resolution_status === 'corrected' && (
                <Button size="sm" className="h-7 text-xs" variant="outline" onClick={() => doAction('mark-resubmission-ready')} data-testid="action-resub-ready">
                  <ArrowRight className="h-3 w-3 mr-1" />Ready to Resubmit
                </Button>
              )}
              {detail.resolution_status === 'resubmission_ready' && (
                <Button size="sm" className="h-7 text-xs" onClick={() => doAction('resubmit')} data-testid="action-resubmit">
                  <Send className="h-3 w-3 mr-1" />Resubmit (New Attempt)
                </Button>
              )}
            </div>
            {['under_review', 'corrected'].includes(detail.resolution_status) && (
              <textarea className="w-full text-xs border rounded p-2 mt-2" rows={2} placeholder="Correction note..."
                value={correctionNote} onChange={e => setCorrectionNote(e.target.value)} data-testid="correction-note" />
            )}
          </CardContent></Card>

          {/* Workflow history */}
          <Card><CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Workflow History</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3">
              <Timeline events={detail.workflow_history} />
          </CardContent></Card>
        </>)}
      </SlidePanel>
    </div>
  );
}

// ═══════════════════════════════════════
// DENIAL STATE BADGE (6A.4-C)
// ═══════════════════════════════════════

export { RejectionsTab };
